import styled from "@emotion/styled"
import { Icon } from "components/icon/Icon"
import Primary from "components/nav-v2/panes/Primary"
import Secondary from "components/nav-v2/panes/Secondary"
import { cssVars, useNav } from "components/nav-v2/Wrapper"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import getMediaQuery, { breakpoint } from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { animate, useMotionValueEvent } from "framer-motion"
import { useLayoutEffect, useRef } from "react"

const Wrapper = styled.div`
  position: relative;
  z-index: 0;
  grid-row: 2/3;
  grid-column: 1/2;

  --pane-width: 100vw;

  ${getMediaQuery("m", { max: false })} {
    grid-row: 1/3;
    --pane-width: clamp(384px, 50vw, 1024px);
  }

  ${getMediaQuery("l", { max: false })} {
    grid-row: 1/3;
    --pane-width: clamp(512px, 33.3vw, 1280px);
  }

  ${getMediaQuery("xl", { max: false })} {
    --pane-width: clamp(512px, 33.3vw, 960px);
  }
`

const Close = styled.div`
  --dir: 1;
  [dir="rtl"] & {
    --dir: -1;
  }

  position: fixed;
  z-index: 9999;
  inset-inline-start: 2rem;
  inset-block-start: 0;
  top: 4.3rem;
  opacity: calc(var(--visible-close, 0) * var(${cssVars.main.panes.expanded}));
  --max-translate: (var(--pane-width) * 2);
  transform: translate3d(calc(var(${cssVars.main.panes.primary.expanded}) * var(--max-translate) * var(--dir)), 0, 0);

  @media (width <= 1100px) {
    --max-translate: calc(var(--pane-width) * 2 - 5.6rem);
  }

  > button {
    ${buttonReset}
    display: flex;
    border-radius: 9999px;
    background-color: rgb(var(--pure-white));
    padding: 0.25rem;
    pointer-events: auto;
    cursor: pointer !important;
    transition: fill 300ms;

    > svg {
      fill: currentColor;
      padding: 0.5rem;
      width: 0.8rem;
      height: 0.8rem;
    }
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      > button > svg {
        fill: rgb(var(--green));
      }
    }
  }

  &:focus {
    outline: 1px solid red;
    outline-offset: -1px;
  }

  content-visibility: hidden;
  @media (min-width: ${breakpoint.m}) {
    content-visibility: visible;
  }
`

const Panes = props => {
  const console = useConsole()
  const nav = useNav()
  const rwrapper = useRef(null)
  const rclose = useRef(null)
  const dictionary = useDictionary()

  const updateCloseDirection = panes => {
    if (!rclose.current) return

    if (panes.has("secondary")) {
      rclose.current.style.setProperty("--visible-close", "0")
    } else if (panes.has("primary")) {
      rclose.current.style.setProperty("--visible-close", "1")
    } else {
      rclose.current.style.removeProperty("--visible-close")
    }
  }

  useMotionValueEvent(nav.main.panes.primary.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()

    if (!previous || !latest) {
      const panes = new Set((rwrapper.current.dataset.panes ?? "").split(",").filter(Boolean))

      if (latest > 0) {
        nav.__internals.dispatchEvent(new Event("primary:focus"))
        panes.add("primary")
      } else {
        panes.delete("primary")
      }

      rwrapper.current.dataset.panes = [...panes].join(",")
      updateCloseDirection(panes)
    }
  })

  useMotionValueEvent(nav.main.panes.secondary.motion.expanded, "change", latest => {
    const previous = nav.main.panes.secondary.motion.expanded.getPrevious()

    if (!previous || !latest) {
      const panes = new Set((rwrapper.current.dataset.panes ?? "").split(",").filter(Boolean))

      if (latest > 0) {
        nav.__internals.dispatchEvent(new Event("secondary:focus"))
        panes.add("secondary")
      } else {
        panes.delete("secondary")
      }

      rwrapper.current.dataset.panes = [...panes].join(",")
      updateCloseDirection(panes)
    }
  })

  useLayoutEffect(() => {
    const controller = new AbortController()

    nav.addEventListener(
      "primary:toggle",
      () => {
        const panes = new Set((rwrapper.current.dataset.panes ?? "").split(",").filter(Boolean))

        if (panes.has("primary") || panes.has("secondary")) {
          nav.collapseAll()
        } else {
          const mql = global.matchMedia(`(min-width: ${breakpoint.m})`)
          const primary = rwrapper.current.querySelector(".pane.primary.main")
          if (mql.matches) {
            primary.dataset.subIn = primary.dataset.subDefault
            nav.main.panes.primary.sub.motion.expanded.set(1) // make sure sub pane is expanded if going to the mobile breakpoint
          }

          animate(nav.main.panes.primary.motion.expanded, 1, { duration: mql.matches ? 0.45 : 0.45, ease: "easeOut" })

          rwrapper.current.inert = false
        }
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "secondary:toggle",
      () => {
        const panes = new Set((rwrapper.current.dataset.panes ?? "").split(",").filter(Boolean))

        if (panes.has("secondary")) {
          nav.collapseAll()
        } else {
          const mql = global.matchMedia(`(min-width: ${breakpoint.m})`)
          animate(nav.main.panes.secondary.motion.expanded, 1, { duration: mql.matches ? 0.45 : 0.45, ease: "easeOut" })
          rwrapper.current.inert = false
        }

        if (panes.has("primary")) {
          animate(nav.main.panes.primary.motion.expanded, 0)
        }
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "collapse:all",
      () => {
        rwrapper.current.inert = true
      },
      { signal: controller.signal }
    )

    rwrapper.current.inert = nav.main.panes.motion.expanded.get() === 0
    return () => controller.abort()
  })

  console.verbose("Nav:Panes(%o)", props)
  return (
    <Wrapper ref={rwrapper} className='panes'>
      <Primary {...props} rclose={rclose} />
      <Close ref={rclose}>
        <button type='button' aria-label={dictionary.close()} onClick={() => nav.collapseAll()}>
          <Icon type='close' />
        </button>
      </Close>
      <Secondary {...props} rclose={rclose} />
    </Wrapper>
  )
}

export default Panes
